import React, { useState, useEffect, useRef } from "react";
import { Bell, CheckCheck, ExternalLink, X, History, ChevronRight } from "lucide-react";
import { UserProfile } from "../../types/userProfile";
import {
  AppNotification,
  subscribeUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  normalizeNotificationTimestamp,
} from "../../services/notificationService";
import { auth } from "../../utils/firebase";
import { NotificationDetailModal } from "./NotificationDetailModal";
import { NotificationHistoryModal } from "./NotificationHistoryModal";

interface NotificationCenterProps {
  userProfile?: UserProfile;
  onNavigate?: (path: string) => void;
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  userProfile,
  onNavigate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedNotifForDetail, setSelectedNotifForDetail] = useState<AppNotification | null>(null);

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const cleanUid = userProfile?.id || auth?.currentUser?.uid || "";
  const cleanEmail = userProfile?.email || auth?.currentUser?.email || "";
  const cleanCpf = userProfile?.cpf || "";

  useEffect(() => {
    if (!cleanUid && !cleanEmail && !cleanCpf) {
      setNotifications([]);
      return;
    }

    const unsubscribe = subscribeUserNotifications(
      { userId: cleanUid, userEmail: cleanEmail, userCpf: cleanCpf },
      (list) => {
        setNotifications(list || []);
      }
    );

    return () => unsubscribe();
  }, [cleanUid, cleanEmail, cleanCpf]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // REQUISITO DEFINITIVO: Ordenar estritamente por createdAt DESC e exibir EXCLUSIVAMENTE as 10 notificações mais recentes no sino
  const sortedNotifications = React.useMemo(() => {
    return [...notifications].sort(
      (a, b) => normalizeNotificationTimestamp(b.createdAt) - normalizeNotificationTimestamp(a.createdAt)
    );
  }, [notifications]);

  const recentNotifications = React.useMemo(() => {
    return sortedNotifications.slice(0, 10);
  }, [sortedNotifications]);

  const handleMarkAsRead = async (notif: AppNotification, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!notif.read) {
      await markNotificationAsRead(notif.id, cleanUid || notif.userId);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsAsRead(notifications, cleanUid);
  };

  const handleNotificationClick = async (notif: AppNotification) => {
    await handleMarkAsRead(notif);
    setSelectedNotifForDetail(notif);
    setIsOpen(false);
  };

  const handleOpenHistory = () => {
    setIsOpen(false);
    setIsHistoryOpen(true);
  };

  const formatTime = (isoString?: string) => {
    if (!isoString) return "";
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return "";
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();

      const timeStr = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
      if (isToday) {
        return `Hoje às ${timeStr}`;
      }
      const dateStr = date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
      return `${dateStr} às ${timeStr}`;
    } catch {
      return "";
    }
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Botão de Sino / Notificações */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="relative p-2 rounded-xl bg-slate-100 dark:bg-emerald-950/60 border border-slate-200 dark:border-emerald-900/50 text-slate-700 dark:text-emerald-300 hover:text-slate-900 dark:hover:text-white transition cursor-pointer flex items-center justify-center"
          title="Notificações"
          aria-label="Abrir central de notificações"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-amber-500 text-emerald-950 font-black text-[10px] min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center border-2 border-white dark:border-[#041208] animate-pulse">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {/* Painel Dropdown de Notificações */}
        {isOpen && (
          <div className="absolute right-0 sm:right-0 mt-2 w-80 sm:w-96 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-2xl shadow-2xl z-50 overflow-hidden text-slate-900 dark:text-white animate-in fade-in slide-in-from-top-2 duration-150 flex flex-col max-h-[85vh]">
            {/* Cabeçalho do Painel */}
            <div className="px-4 py-3 bg-slate-50 dark:bg-zinc-950/80 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Bell size={16} className="text-amber-500" />
                <span className="font-display font-black text-sm text-slate-900 dark:text-white">
                  Notificações
                </span>
                {unreadCount > 0 && (
                  <span className="bg-amber-400/20 text-amber-700 dark:text-amber-300 font-extrabold text-[10px] px-2 py-0.5 rounded-full border border-amber-400/40">
                    {unreadCount} nova(s)
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAllRead}
                    className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline px-2 py-1 rounded-lg transition flex items-center gap-1 cursor-pointer"
                    title="Marcar todas como lidas"
                  >
                    <CheckCheck size={14} />
                    <span className="hidden sm:inline">Ler todas</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg transition cursor-pointer"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Lista com Limite de 10 Mais Recentes */}
            <div className="overflow-y-auto divide-y divide-slate-100 dark:divide-zinc-800/80 flex-1">
              {recentNotifications.length === 0 ? (
                <div className="py-10 px-4 text-center space-y-2">
                  <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mx-auto text-slate-400">
                    <Bell size={20} />
                  </div>
                  <p className="text-xs font-bold text-slate-600 dark:text-zinc-400">
                    Nenhuma notificação no momento.
                  </p>
                </div>
              ) : (
                recentNotifications.map((notif) => {
                  const isUnread = !notif.read;
                  return (
                    <div
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`p-3.5 transition cursor-pointer flex gap-3 items-start ${
                        isUnread
                          ? "bg-amber-500/5 dark:bg-amber-500/10 hover:bg-amber-500/10 dark:hover:bg-amber-500/15"
                          : "hover:bg-slate-50 dark:hover:bg-zinc-800/50 opacity-80 hover:opacity-100"
                      }`}
                    >
                      {/* Ponto indicador de não lida */}
                      <div className="mt-1 shrink-0">
                        {isUnread ? (
                          <span className="w-2.5 h-2.5 rounded-full bg-amber-500 block ring-2 ring-amber-400/30" />
                        ) : (
                          <span className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-zinc-700 block" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0 space-y-0.5">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-xs font-black text-slate-900 dark:text-white truncate">
                            {notif.title}
                          </h4>
                          <span className="text-[10px] text-slate-400 dark:text-zinc-500 whitespace-nowrap shrink-0">
                            {formatTime(notif.createdAt)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-zinc-300 line-clamp-2 leading-relaxed font-medium">
                          {notif.message}
                        </p>

                        {notif.linkUrl && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 dark:text-amber-400 hover:underline pt-0.5">
                            <span>Ver detalhes</span>
                            <ExternalLink size={10} />
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Rodapé — Ação de Abrir Histórico Completo */}
            <div className="p-3 bg-slate-50 dark:bg-zinc-950 border-t border-slate-200 dark:border-zinc-800 shrink-0 text-center">
              <button
                type="button"
                onClick={handleOpenHistory}
                className="w-full py-2 px-3 bg-slate-200/80 dark:bg-zinc-800 hover:bg-amber-400 dark:hover:bg-amber-400 hover:text-emerald-950 text-slate-800 dark:text-zinc-200 font-extrabold text-xs rounded-xl transition flex items-center justify-center gap-2 cursor-pointer shadow-sm group"
              >
                <History size={15} className="text-amber-500 group-hover:text-emerald-950 transition" />
                <span>Ver histórico completo ({notifications.length})</span>
                <ChevronRight size={14} className="opacity-70 group-hover:translate-x-0.5 transition" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Histórico de Notificações */}
      <NotificationHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        notifications={notifications}
        cleanUid={cleanUid}
        onNavigate={onNavigate}
      />

      {/* Modal de Detalhe da Notificação Selecionada */}
      <NotificationDetailModal
        notification={selectedNotifForDetail}
        onClose={() => setSelectedNotifForDetail(null)}
        onNavigate={onNavigate}
        cleanUid={cleanUid}
      />
    </>
  );
};
